(() => {
  'use strict';

  /*
    Curated collection landing page

    What this version does:
    - Keeps Shopify's existing collection grid visible
    - Moves the clicked product to the first position if it is already loaded
    - Fetches only the clicked product if it is not already loaded
    - Keeps the collection at the requested limit
    - Preserves variant links and the variant image update
    - Does not rebuild the whole collection
  */

  const GRID_SELECTORS = [
    'ul#product-grid',
    'ul[id^="product-grid"]',
    '.product-grid'
  ];

  const grid = GRID_SELECTORS
    .map(selector => document.querySelector(selector))
    .find(Boolean);

  if (!grid) return;

  const query = new URLSearchParams(window.location.search);

  let rawFirst = (query.get('first') || '').trim();
  if (!rawFirst) return;

  /*
    URLSearchParams normally decodes first= for us.
    This also supports a value that has been encoded twice.
  */
  try {
    if (/%[0-9a-f]{2}/i.test(rawFirst)) {
      rawFirst = decodeURIComponent(rawFirst);
    }
  } catch (_) {
    // Keep the original value if decoding fails.
  }

  let firstHandle = '';
  let variantFromProductURL = '';

  /*
    Supports either:

    first=product-handle

    or:

    first=https://unleashedthreads.co/products/product-handle?variant=123
  */
  if (/^https?:\/\//i.test(rawFirst)) {
    try {
      const productURL = new URL(rawFirst);

      const pathParts = productURL.pathname
        .split('/')
        .filter(Boolean);

      if (pathParts[0] === 'products' && pathParts[1]) {
        firstHandle = pathParts[1];
      }

      variantFromProductURL =
        productURL.searchParams.get('variant') || '';
    } catch (_) {
      return;
    }
  } else {
    const pathParts = rawFirst
      .split('/')
      .filter(Boolean);

    firstHandle = pathParts[pathParts.length - 1] || '';
  }

  if (!firstHandle) return;

  const possibleVariant =
    query.get('variant') || variantFromProductURL;

  const variantID = /^\d+$/.test(possibleVariant)
    ? possibleVariant
    : '';

  /*
    Keep the existing URL behavior.

    If limit=16 is supplied by Meta, the page will retain
    up to 16 product cards. Without it, the current default
    remains 24.
  */
  const requestedLimit = parseInt(
    query.get('limit') || '24',
    10
  );

  const limit = Number.isFinite(requestedLimit) &&
    requestedLimit > 0
    ? requestedLimit
    : 24;

  /*
    Preserve theme-editor preview parameters.
  */
  const previewParameters = [
    'preview_theme_id',
    'preview_theme_token',
    'pb'
  ]
    .filter(key => query.has(key))
    .map(key => {
      return `${key}=${encodeURIComponent(query.get(key) || '')}`;
    })
    .join('&');

  const addPreviewParameters = url => {
    if (!previewParameters) return url;

    return url.includes('?')
      ? `${url}&${previewParameters}`
      : `${url}?${previewParameters}`;
  };

  const previewRequest = Boolean(previewParameters);

/*
  Use the same view as the normal collection cards.

  Start with card-portrait because the collection cards
  are using a taller product-image layout.

  If your normal cards are square, use:
  'card-square'
*/
const cardView = 'card-portrait';

  /*
    Return only actual product-card elements.
  */
  const getCards = () => {
    return Array.from(grid.children)
      .filter(element => {
        return element.matches &&
          element.matches('.grid__item');
      });
  };

  /*
    Extract a Shopify product handle from a product link.
  */
  const getHandleFromLink = link => {
    if (!link) return '';

    try {
      const url = new URL(
        link.getAttribute('href'),
        window.location.origin
      );

      const match = url.pathname.match(
        /^\/products\/([^/]+)/
      );

      return match
        ? decodeURIComponent(match[1])
        : '';
    } catch (_) {
      return '';
    }
  };

  /*
    Find the product handle represented by a card.
  */
  const getHandleFromCard = card => {
    const productLinks = card.querySelectorAll(
      'a[href*="/products/"]'
    );

    for (const link of productLinks) {
      const handle = getHandleFromLink(link);

      if (handle) return handle;
    }

    return '';
  };

  /*
    Session cache for the one card we may need to fetch.
  */
  const cacheKey = (
    handle,
    variant
  ) => {
    return `curated-card:${cardView}:${handle}:${variant || 'default'}`;
  };

  const getCachedCard = (
    handle,
    variant
  ) => {
    try {
      return sessionStorage.getItem(
        cacheKey(handle, variant)
      );
    } catch (_) {
      return null;
    }
  };

  const cacheCard = (
    handle,
    variant,
    html
  ) => {
    try {
      sessionStorage.setItem(
        cacheKey(handle, variant),
        html
      );
    } catch (_) {
      // Ignore storage quota or privacy-mode errors.
    }
  };

  /*
    Fetch only the clicked product card when it is not
    already present in Shopify's initial collection grid.
  */
  const fetchProductCard = async (
    handle,
    variant = ''
  ) => {
    const cached = getCachedCard(handle, variant);

    if (cached) return cached;

    const parameters = new URLSearchParams();
    parameters.set('view', cardView);

    if (variant) {
      parameters.set('variant', variant);
    }

    const url = addPreviewParameters(
      `/products/${encodeURIComponent(handle)}?${parameters}`
    );

    const response = await fetch(url, {
      credentials: previewRequest
        ? 'same-origin'
        : 'omit',
      cache: 'force-cache'
    });

    if (!response.ok) return '';

    const html = await response.text();

    if (html.trim()) {
      cacheCard(handle, variant, html);
    }

    return html;
  };

  /*
    Convert the fetched card HTML into a DOM element.
  */
  const cardFromHTML = html => {
    if (!html || !html.trim()) return null;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return Array.from(template.content.children)
      .find(element => {
        return element.matches &&
          element.matches('.grid__item');
      }) || null;
  };

  /*
    Add the selected variant to links in the pinned card.
  */
  const updateCardLinks = (
    card,
    handle,
    variant
  ) => {
    if (!card || !variant) return;

    card.querySelectorAll(
      'a[href*="/products/"]'
    ).forEach(link => {
      const linkHandle = getHandleFromLink(link);

      if (linkHandle !== handle) return;

      try {
        const url = new URL(
          link.getAttribute('href'),
          window.location.origin
        );

        url.searchParams.set('variant', variant);

        link.setAttribute(
          'href',
          `${url.pathname}${url.search}${url.hash}`
        );
      } catch (_) {
        // Ignore malformed links.
      }
    });
  };

  /*
    Update the pinned card's image for the clicked variant.

    This runs after the card has already been placed,
    so it does not delay the collection grid appearing.
  */
  const updateVariantImage = async (
    card,
    handle,
    variant
  ) => {
    if (!card || !variant) return;

    try {
      const response = await fetch(
        `/products/${encodeURIComponent(handle)}.js`,
        {
          cache: 'force-cache'
        }
      );

      if (!response.ok) return;

      const product = await response.json();

      const selectedVariant = Array.isArray(product.variants)
        ? product.variants.find(item => {
            return String(item.id) === String(variant);
          })
        : null;

      const imageURL =
        selectedVariant &&
        selectedVariant.featured_image &&
        selectedVariant.featured_image.src;

      if (!imageURL) return;

      card.querySelectorAll('img').forEach(image => {
        image.src = imageURL;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.loading = 'eager';
        image.fetchPriority = 'high';
      });
    } catch (_) {
      // Keep the normal card image if the variant image fails.
    }
  };

  /*
    Keep no more than the requested number of cards.
  */
  const trimGrid = () => {
    let cards = getCards();

    while (cards.length > limit) {
      const lastCard = cards.pop();

      if (lastCard) {
        lastCard.remove();
      }
    }
  };

  /*
    Main process.
  */
  (async () => {
    let cards = getCards();

    /*
      First try to reuse a product card Shopify already rendered.
    */
    let pinnedCard = cards.find(card => {
      return getHandleFromCard(card) === firstHandle;
    });

    /*
      If the clicked product is not in the initial page,
      fetch only that one product card.
    */
    if (!pinnedCard) {
      const html = await fetchProductCard(
        firstHandle,
        variantID
      );

      pinnedCard = cardFromHTML(html);

      if (pinnedCard) {
        const firstExistingCard = getCards()[0];

        if (firstExistingCard) {
          grid.insertBefore(
            pinnedCard,
            firstExistingCard
          );
        } else {
          grid.appendChild(pinnedCard);
        }
      }
    }

    /*
      If the product could not be found, leave the normal
      Shopify collection grid untouched.
    */
    if (!pinnedCard) {
      console.warn(
        'Curated collection: pinned product card could not be loaded.',
        firstHandle
      );

      return;
    }

    /*
      Move the clicked product to the beginning.
    */
    const firstCard = getCards()[0];

    if (pinnedCard !== firstCard) {
      grid.insertBefore(
        pinnedCard,
        firstCard || null
      );
    }

    /*
      Keep the requested number of products.
    */
    trimGrid();

    /*
      Make the pinned product link open the clicked variant.
    */
    updateCardLinks(
      pinnedCard,
      firstHandle,
      variantID
    );

    /*
      Adjust the image without blocking the initial page display.
    */
    if (variantID) {
      updateVariantImage(
        pinnedCard,
        firstHandle,
        variantID
      );
    }
  })().catch(error => {
    /*
      Fail quietly and leave Shopify's normal grid visible.
      A failed optimization should never leave the shopper
      looking at a blank collection page.
    */
    console.warn(
      'Curated collection optimization failed:',
      error
    );
  });
})();
